import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ScoreRing } from "@/components/ScoreRing";
import { InfoTip } from "@/components/InfoTip";
import { MatchBreakdown } from "@/pages/candidate/Jobs";
import { GitCompare, Loader2, Bookmark, Trash2 } from "lucide-react";

export function JobCompare({ base, jobsUrl, careerScore }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.get(jobsUrl).then((r) => setJobs((r.data.jobs || []).filter((j) => j.active !== false))).catch(() => setJobs([]));
    api.get(`${base}/comparisons`).then((r) => setSaved(r.data.comparisons || [])).catch(() => {});
  }, [base, jobsUrl]);

  const run = async (save = false) => {
    if (!jobId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/compare${save ? "?save=true" : ""}`, { job_id: jobId });
      setRes(data);
      if (save) { setSaved((s) => [data, ...s]); toast.success("Porovnanie bolo uložené do profilu"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Porovnanie zlyhalo"); }
    finally { setBusy(false); }
  };
  const pick = (id) => { setJobId(id); setRes(null); };
  useEffect(() => { if (jobId) run(false); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    await api.delete(`${base}/comparisons/${id}`).catch(() => {});
    setSaved((s) => s.filter((c) => c.id !== id));
  };

  return (
    <div className="mb-5 rounded-3xl bg-white border border-slate-200 shadow-sm p-6" data-testid="job-compare-card">
      <h3 className="font-head font-semibold text-lg flex items-center gap-2">
        <GitCompare size={18} className="text-blue-700" /> Porovnanie s ponukou
        <InfoTip text="Career Score hodnotí celkovú kvalitu profilu. Match Score ukazuje zhodu s vybranou pracovnou ponukou (deterministický výpočet)." />
      </h3>
      <select data-testid="compare-job-select" value={jobId} onChange={(e) => pick(e.target.value)}
        className="mt-4 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100">
        <option value="">{jobs.length ? "Vyberte pracovnú ponuku…" : "Žiadne dostupné ponuky"}</option>
        {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}{j.company_name ? ` · ${j.company_name}` : ""}</option>)}
      </select>
      {busy && !res && <div className="mt-4 flex justify-center"><Loader2 className="animate-spin text-blue-700" /></div>}
      {res && <CompareResult res={res} careerScore={careerScore} busy={busy} onSave={() => run(true)} />}
      <SavedList saved={saved} onRemove={remove} />
    </div>
  );
}

function CompareResult({ res, careerScore, busy, onSave }) {
  return (
    <div className="mt-5" data-testid="compare-result">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3 flex justify-center" data-testid="compare-career-score">
          <ScoreRing value={careerScore || 0} size={96} stroke={8} label="Career Score" />
        </div>
        <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3 flex justify-center" data-testid="compare-match-score">
          <ScoreRing value={res.match_score || 0} size={96} stroke={8} label="Match Score" />
        </div>
      </div>
      <MatchBreakdown j={{ ...res, id: res.job_id }} />
      <button data-testid="compare-save-btn" onClick={onSave} disabled={busy}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-700 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-800 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Bookmark size={16} />} Uložiť do profilu
      </button>
    </div>
  );
}

function SavedList({ saved, onRemove }) {
  if (!saved.length) return null;
  return (
    <div className="mt-5" data-testid="saved-comparisons">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Uložené porovnania</div>
      <ul className="space-y-2">
        {saved.map((c) => (
          <li key={c.id} data-testid={`saved-comparison-${c.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-3 py-2.5">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-700">{c.match_score}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">{c.job_title}</div>
              <div className="text-xs text-slate-500 truncate">{[c.company_name, new Date(c.created_at).toLocaleDateString("sk-SK")].filter(Boolean).join(" · ")}</div>
            </div>
            <button data-testid={`delete-comparison-${c.id}`} onClick={() => onRemove(c.id)} aria-label="Odstrániť"
              className="h-8 w-8 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors"><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
